import { Analytics } from '$lib/services/analytics/client';
import { Sentry } from '$lib/services/sentry';
import { getSubscriptionInfo } from '$lib/stores/subscription/action';
import { SUBSCRIPTION_RENEWAL_TERM, SUBSCRIPTION_STATUS } from '$lib/stores/subscription/constants';
import { app } from '$lib/stores/app/store';
import { ACCOUNT_STATUS } from '$lib/stores/user/constants';
import { user } from '$lib/stores/user/store';
import { QUERY_PARAM_KEYS } from '$lib/utils/constants';
import { get } from 'svelte/store';
import { SUBSCRIPTION_CATEGORY } from '../../../routes/cancel-subscription/__modules/constants';
import { setAppVersion } from '$lib/stores/app/actions';
import { EVENT_TYPES } from '$events';

const AUTH_POLICY_ACTION = 'auth-policies';

/**
 * Reports a policy evaluation failure to Sentry without blocking the route.
 * @param {string} policy - Policy name for the `extra.policy` tag.
 * @param {unknown} error - Caught exception from the policy body.
 */
function reportPolicyException(policy, error) {
	Sentry.captureException(error, {
		extra: { action: AUTH_POLICY_ACTION, policy },
	});
}

// TODO: type of all these and other page based policies

export const ALLOW_ALL_NO_VALIDATE = async () => {
	return true;
};

export const ACTIVE_USERS_ONLY = async () => {
	try {
		const { accountStatus, tokenValid } = get(user);
		return tokenValid && accountStatus === ACCOUNT_STATUS.ACTIVE;
	} catch (error) {
		reportPolicyException('ACTIVE_USERS_ONLY', error);
		return false;
	}
};

export const ALLOW_ALL = async () => {
	return true;
};

export const CANCELLED_USERS_ONLY = async () => {
	try {
		const { accountStatus, tokenValid } = get(user);
		const isEligible =
			tokenValid &&
			[ACCOUNT_STATUS.INACTIVE, ACCOUNT_STATUS.AUXILIARY_ONLY_ACTIVE].includes(accountStatus);

		if (!isEligible) {
			await Analytics.log(EVENT_TYPES.CLIENT_ACCESS_DENIED, {
				policy: 'CANCELLED_USERS_ONLY',
				reason: 'ineligible_for_reactivation',
				account_status: accountStatus,
				token_valid: tokenValid,
				is_eligible: isEligible,
			});
		}

		return isEligible;
	} catch (error) {
		reportPolicyException('CANCELLED_USERS_ONLY', error);
		return false;
	}
};

export const VALID_NO_ACTIVE_PRIMARY_SUBSCRIPTION_ONLY = async () => {
	try {
		const { accountStatus, tokenValid } = get(user);
		return (
			tokenValid &&
			(ACCOUNT_STATUS.FREE === accountStatus ||
				ACCOUNT_STATUS.INACTIVE === accountStatus ||
				ACCOUNT_STATUS.AUXILIARY_ONLY_ACTIVE === accountStatus)
		);
	} catch (error) {
		reportPolicyException('VALID_NO_ACTIVE_PRIMARY_SUBSCRIPTION_ONLY', error);
		return false;
	}
};

export const LOGGED_OUT_USERS_ONLY = async () => {
	try {
		const currentUser = get(user);
		return !currentUser.tokenValid;
	} catch (error) {
		reportPolicyException('LOGGED_OUT_USERS_ONLY', error);
		return false;
	}
};

export const USERS_WITHOUT_CREDENTIALS_ONLY = async () => {
	try {
		const { tokenValid, hasCredentials } = get(user);
		return !tokenValid || !hasCredentials;
	} catch (error) {
		reportPolicyException('USERS_WITHOUT_CREDENTIALS_ONLY', error);
		return false;
	}
};

export const LOGGED_IN_USERS_ONLY = async () => {
	try {
		const currentUser = get(user);
		return currentUser.tokenValid;
	} catch (error) {
		reportPolicyException('LOGGED_IN_USERS_ONLY', error);
		return false;
	}
};

export const VALIDATE_ABCMOUSE_SERVICE_LAYER_VIEW = async () => {
	try {
		const url = new URL(window?.location?.href);
		const appVersion = url.searchParams.get(QUERY_PARAM_KEYS.APP_VERSION);

		if (!appVersion) {
			return false;
		}

		setAppVersion(appVersion);
		const currentUser = get(user);
		return currentUser.tokenValid;
	} catch (error) {
		reportPolicyException('VALIDATE_ABCMOUSE_SERVICE_LAYER_VIEW', error);
		return false;
	}
};

export const VALID_PARENT_SECTION_USERS = async () => {
	try {
		const currentUser = get(user);
		return currentUser.tokenValid;
	} catch (error) {
		reportPolicyException('VALID_PARENT_SECTION_USERS', error);
		return false;
	}
};

export const VALID_CANCEL_SUBSCRIPTION_USERS = async () => {
	try {
		const currentUser = get(user);
		const isUserValid = currentUser.tokenValid;

		const url = new URL(window?.location?.href);
		const subscriptionCategory =
			url.searchParams.get(QUERY_PARAM_KEYS.SUBSCRIPTION_CATEGORY) || SUBSCRIPTION_CATEGORY.PRIMARY;

		// Get the subscription UUID for the subscription category
		const foundAccount = currentUser?.aoflProducts?.find((/** @type {any} */ account) => {
			return account.subscriptionPlanCategory === subscriptionCategory;
		});

		const subscriptionUuid = foundAccount?.subscriptionUuid ?? '';

		const { data } = await getSubscriptionInfo({ subscription_uuid: subscriptionUuid });

		const isSubscriptionActive = data?.subscription_status === SUBSCRIPTION_STATUS.ACTIVE;

		return isUserValid && isSubscriptionActive;
	} catch (error) {
		reportPolicyException('VALID_CANCEL_SUBSCRIPTION_USERS', error);
		return false;
	}
};

export const VALID_MONTHLY_SUBSCRIPTION_USERS = async () => {
	try {
		const currentUser = get(user);
		const isUserValid = currentUser.tokenValid;

		const { data } = await getSubscriptionInfo();

		const renewalTerm = data?.renewal_term;
		const renewalPeriod = data?.renewal_period;

		const isMonthlySubscription =
			renewalTerm === SUBSCRIPTION_RENEWAL_TERM.month && renewalPeriod === 1;

		return isUserValid && isMonthlySubscription;
	} catch (error) {
		reportPolicyException('VALID_MONTHLY_SUBSCRIPTION_USERS', error);
		return false;
	}
};

export const VALID_SUPPORT_CHATBOT_USERS = async () => {
	try {
		const currentUser = get(user);
		return currentUser.tokenValid;
	} catch (error) {
		reportPolicyException('VALID_SUPPORT_CHATBOT_USERS', error);
		return false;
	}
};

export const FREE_USER_WITH_CREDENTIALS = async () => {
	try {
		const { tokenValid, hasCredentials, accountStatus } = get(user);
		return tokenValid && hasCredentials && accountStatus === ACCOUNT_STATUS.FREE;
	} catch (error) {
		reportPolicyException('FREE_USER_WITH_CREDENTIALS', error);
		return false;
	}
};

/**
 * CMS/API may send boolean true or string "true" for special-offer `reactivationsAllowed` (from pre-unwrapped `custom_fields`).
 * @param {unknown} value - Raw flag from offer custom fields.
 * @returns {boolean}
 */
function isTruthyReactivationsAllowed(value) {
	return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}

/**
 * Subscription offer page: in-app users always allowed; browser users allowed if
 * FREE with credentials, or INACTIVE when the offer permits reactivations.
 * @param {{ reactivationsAllowed?: unknown }} offerData - Offer data from the API (e.g. special offer template `custom_fields`).
 * @returns {Promise<boolean>}
 */
export const VALID_SUBSCRIPTION_OFFER_USERS = async (offerData) => {
	try {
		if (get(app).meta.inApp) return true;
		const { accountStatus, hasCredentials } = get(user);
		const isFreeOrAux =
			accountStatus === ACCOUNT_STATUS.FREE ||
			accountStatus === ACCOUNT_STATUS.AUXILIARY_ONLY_ACTIVE;
		const isInactive = accountStatus === ACCOUNT_STATUS.INACTIVE;
		const reactivationsAllowed = isTruthyReactivationsAllowed(offerData?.reactivationsAllowed);
		return (isFreeOrAux && hasCredentials) || (isInactive && reactivationsAllowed);
	} catch (error) {
		reportPolicyException('VALID_SUBSCRIPTION_OFFER_USERS', error);
		return false;
	}
};
